/**
 * AIProviderCard
 *
 * Card displaying an AI provider's configuration status.
 * Shows configured/not configured state with actions.
 *
 * @module ai-settings/components
 */

import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Box,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import type { AIProviderInfo, AIKeyMetadata, AIProviderType } from '../types/aiKey.types';

interface AIProviderCardProps {
  provider: AIProviderInfo;
  metadata: AIKeyMetadata;
  onAdd: (provider: AIProviderType) => void;
  onEdit: (provider: AIProviderType) => void;
  onDelete: (provider: AIProviderType) => void;
  disabled?: boolean;
}

export const AIProviderCard: React.FC<AIProviderCardProps> = ({
  provider,
  metadata,
  onAdd,
  onEdit,
  onDelete,
  disabled,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const handleMenuClose = () => setAnchorEl(null);

  return (
    <Card variant="outlined" sx={{ mb: 1.5 }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {provider.name}
            </Typography>
            <Chip
              label={metadata.configured ? 'Configured' : 'Not configured'}
              size="small"
              color={metadata.configured ? 'success' : 'default'}
              variant={metadata.configured ? 'filled' : 'outlined'}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {provider.description}
          </Typography>
          {metadata.configured && metadata.maskedHint && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Key: {metadata.maskedHint}
            </Typography>
          )}
        </Box>

        {metadata.configured ? (
          <>
            <IconButton
              size="small"
              onClick={(e) => setAnchorEl(e.currentTarget)}
              disabled={disabled}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={menuOpen}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={() => { handleMenuClose(); onEdit(provider.type); }}>
                <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Edit Key</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => { handleMenuClose(); onDelete(provider.type); }}>
                <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                <ListItemText sx={{ color: 'error.main' }}>Remove Key</ListItemText>
              </MenuItem>
            </Menu>
          </>
        ) : (
          <IconButton
            color="primary"
            onClick={() => onAdd(provider.type)}
            disabled={disabled}
          >
            <AddIcon />
          </IconButton>
        )}
      </CardContent>
    </Card>
  );
};
